import { ArrowRight, Check, X } from "lucide-react";
import { SMS_ADDON_MONTHLY_PRICE, SMS_ADDON_YEARLY_PRICE, getAmountDueAtCheckout, getMonthlyEquivalentPrice, getSmsAddonAmountForInterval, } from "../../hooks/usePricingCheckout";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

// Pre-checkout dialog for paid plans: order summary, SMS add-on toggle, proceed to Stripe.
// All behaviour comes from usePricingCheckout(); this is markup only.
export function CheckoutModal({ modal, isYearly }) {
    const { plan, isOpen, isProcessing, includeSmsAddon } = modal;
    if (!isOpen || !plan) return null;
    const planTitle = plan.name?.toLowerCase().includes("plan") ? plan.name : `${plan.name} Plan`;
    const smsAddonDue = includeSmsAddon ? getSmsAddonAmountForInterval(isYearly) : 0;
    const dueToday = getAmountDueAtCheckout(plan, isYearly) + smsAddonDue;
    return (<div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/70 px-4 backdrop-blur-sm" onClick={(e) => {
            if (e.target === e.currentTarget) modal.close();
        }}>
      <div role="dialog" aria-modal="true" aria-labelledby="checkout-modal-title" className="relative w-full max-w-md rounded-2xl bg-card p-7 text-card-foreground shadow-lift">
        <button type="button" onClick={modal.close} disabled={isProcessing} aria-label="Close modal" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50">
          <X className="h-4 w-4"/>
        </button>

        <h2 id="checkout-modal-title" className="pr-8 text-2xl font-bold">{planTitle}</h2>

        <div className="mt-5 space-y-3 rounded-xl border border-border bg-secondary/50 p-4 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <span>{planTitle} ({isYearly ? "Yearly" : "Monthly"})</span>
            <span className="font-semibold">
              ${getMonthlyEquivalentPrice(plan, isYearly).toFixed(2)}
              <small className="ml-1 text-xs font-normal text-muted-foreground">
                {isYearly ? "/mo (billed yearly)" : "/mo"}
              </small>
            </span>
          </div>
          {includeSmsAddon && (<div className="flex items-baseline justify-between gap-4">
              <span>SMS Instant Alerts Add-On</span>
              <span className="font-semibold">
                +${smsAddonDue.toFixed(2)}
                <small className="ml-1 text-xs font-normal text-muted-foreground">
                  {isYearly ? "/yr (10% off)" : "/mo"}
                </small>
              </span>
            </div>)}
          {/* The amount collected for the selected period - on yearly, full annual plan + SMS year. */}
          <div className="flex items-baseline justify-between gap-4 border-t border-border pt-3 font-bold">
            <span>Total Due Today</span>
            <span>
              ${dueToday.toFixed(2)}
              <small className="ml-1 text-xs font-normal text-muted-foreground">
                {isYearly ? "(billed annually)" : "/mo"}
              </small>
            </span>
          </div>
        </div>

        <div role="button" tabIndex={0} onClick={modal.toggleSms} onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                modal.toggleSms();
            }
        }} className={cn("mt-4 flex cursor-pointer gap-3 rounded-xl border p-4 text-left transition-colors hover:border-foreground/30", includeSmsAddon ? "border-signal bg-signal/15" : "border-border")}>
          <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border", includeSmsAddon ? "border-signal bg-signal text-ink" : "border-input bg-background")}>
            {includeSmsAddon && <Check className="h-3.5 w-3.5"/>}
          </span>
          <span>
            <span className="flex flex-wrap items-center gap-2 text-sm font-bold">
              Add SMS Alerts Package
              <span className="rounded-full bg-signal px-2 py-0.5 text-xs">
                +${(isYearly ? SMS_ADDON_YEARLY_PRICE : SMS_ADDON_MONTHLY_PRICE).toFixed(2)}{isYearly ? "/yr" : "/mo"}
              </span>
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
              Receive instantaneous mobile text alerts for Stop Work Orders, Emergency Vacates, and scheduled court hearings.
            </span>
          </span>
        </div>

        <Button type="button" variant="signal" size="lg" className="mt-6 w-full" onClick={modal.checkout} disabled={isProcessing}>
          {isProcessing ? "Connecting to Checkout…" : (<>
              Proceed to Checkout <ArrowRight />
            </>)}
        </Button>
      </div>
    </div>);
}
